import { AxiosResponse } from "axios";
import { OrganizationKeyName } from "../../../model/Organization";
import { axiosAuthenticated, httpClient } from "../../../service/http";
import {
  CreateMenuData,
  DeleteMenuData,
  MenuCampaign,
  MenuDraftResetOrPublish,
  MenuOverwrite,
  MenuProductsSuccessResponse,
  MenuRecommendedProduct,
  MenuRecommendedProductsSuccessResponse,
  MenuSalesChannel,
  MenuSalesChannelsSuccessResponse,
  MenuSuccessResponse,
  MenusSuccessResponse,
  MutateMenuOverwritePayload,
  PublishDestinations,
  PublishMenusToDestinations,
  PushMenuOnSalesChannelData,
  PushMenuToSalesChannelsData,
  PushMenusToSalesDestinationsData,
  SalesChannelsFromMenuSuccessResponse,
  UpdateMenuData,
  UpdateMenuDraftProducts,
  UpdateRecommendedProductsPayload,
} from "../models/Menus";
import {
  DeletePayload,
  PostPayload,
  PutPayload,
} from "../../../model/http-crud-models";

export const menuHttp = {
  getMenus: async (organizationKeyName: OrganizationKeyName) => {
    const url = `/organizations/${organizationKeyName}/shop-menus/items`;
    const response = await axiosAuthenticated.get<
      any,
      AxiosResponse<MenusSuccessResponse>
    >(url);
    const menus = response.data.data;
    return menus;
  },

  getMenu: async (organizationKeyName: OrganizationKeyName, id?: string) => {
    const url = `/organizations/${organizationKeyName}/shop-menus/items/${id}`;
    const response = await axiosAuthenticated.get<
      any,
      AxiosResponse<MenuSuccessResponse>
    >(url);
    const menus = response.data.data;
    return menus;
  },

  getMenuProducts: async (
    organizationKeyName: OrganizationKeyName,
    id?: string
  ) => {
    const url = `/organizations/${organizationKeyName}/shop-menus/items/${id}/products`;
    const response = await axiosAuthenticated.get<
      any,
      AxiosResponse<MenuProductsSuccessResponse>
    >(url);
    const menus = response.data.data;
    return menus;
  },

  getMenuDraftProducts: async (
    organizationKeyName: OrganizationKeyName,
    id?: string
  ) => {
    const url = `/organizations/${organizationKeyName}/shop-menus/items/${id}/draft-products`;
    const response = await axiosAuthenticated.get<
      any,
      AxiosResponse<MenuProductsSuccessResponse>
    >(url);
    const menus = response.data.data;
    return menus;
  },

  updateMenuDraftProducts: async ({
    organizationKeyName,
    data,
    id,
  }: UpdateMenuDraftProducts) => {
    const url = `/organizations/${organizationKeyName}/shop-menus/items/${id}/draft-products`;
    const response = await axiosAuthenticated.put<any, AxiosResponse>(
      url,
      data
    );

    return response;
  },

  getAvailableMenuProducts: async (
    organizationKeyName: OrganizationKeyName,
    id?: string,
    filters?: { tags?: string[] }
  ) => {
    const params = new URLSearchParams();
    if (filters?.tags) {
      filters.tags.forEach((tag) => {
        params.append("tags[]", tag);
      });
    }
    const url = `/organizations/${organizationKeyName}/shop-menus/items/${id}/available-products?${params.toString()}`;
    const response = await axiosAuthenticated.get<
      any,
      AxiosResponse<MenuProductsSuccessResponse>
    >(url);
    const menus = response.data.data;
    return menus;
  },

  getPublishDestinations: async (organizationKeyName: OrganizationKeyName) => {
    const url = `/organizations/${organizationKeyName}/shop-menus/publish-destinations`;
    const response = await axiosAuthenticated.get<
      any,
      AxiosResponse<PublishDestinations>
    >(url);
    const publishDestinations = response.data;
    return publishDestinations;
  },

  getMenuSalesChannels: async (
    organizationKeyName: OrganizationKeyName,
    id?: string
  ) => {
    const url = `/organizations/${organizationKeyName}/shop-menus/items/${id}/sales-channels`;
    const response = await axiosAuthenticated.get<
      any,
      AxiosResponse<MenuSalesChannelsSuccessResponse>
    >(url);
    const menus = response.data.data;
    return menus;
  },

  createMenu: async ({
    organizationKeyName,
    data,
  }: CreateMenuData): Promise<string> => {
    const url = `/organizations/${organizationKeyName}/shop-menus/items`;
    const response = await axiosAuthenticated.post<
      any,
      AxiosResponse<{ id: string }>
    >(url, data);

    return response.data.id;
  },

  updateMenu: async ({ organizationKeyName, data, id }: UpdateMenuData) => {
    const url = `/organizations/${organizationKeyName}/shop-menus/items/${id}`;
    const response = await axiosAuthenticated.put<any, AxiosResponse>(
      url,
      data
    );

    return response;
  },

  deleteMenu: async ({ id, organizationKeyName }: DeleteMenuData) => {
    const url = `/organizations/${organizationKeyName}/shop-menus/items/${id}/archive`;
    const response = await axiosAuthenticated.post<any, AxiosResponse>(url);

    return response;
  },

  publishMenuDraft: async ({
    id,
    organizationKeyName,
  }: MenuDraftResetOrPublish) => {
    const url = `/organizations/${organizationKeyName}/shop-menus/items/${id}/draft-publish`;
    const response = await axiosAuthenticated.post<any, AxiosResponse>(url);

    return response;
  },

  publishMenusToDestinations: async ({
    organizationKeyName,
    destinations,
  }: PublishMenusToDestinations) => {
    const url = `/organizations/${organizationKeyName}/shop-menus/publish-destinations`;
    const response = await axiosAuthenticated.put<any, AxiosResponse>(url, {
      destinations,
    });

    return response;
  },

  resetMenuDraft: async ({
    id,
    organizationKeyName,
  }: MenuDraftResetOrPublish) => {
    const url = `/organizations/${organizationKeyName}/shop-menus/items/${id}/draft-reset`;
    const response = await axiosAuthenticated.post<any, AxiosResponse>(url);

    return response;
  },

  pushMenuOnSalesChannel: async ({
    salesChannelId,
    organizationKeyName,
  }: PushMenuOnSalesChannelData): Promise<any> => {
    const url = `/organizations/${organizationKeyName}/sales-channels/items/${salesChannelId}/push-menu`;
    const response = await axiosAuthenticated.post<any, AxiosResponse>(url);

    return response;
  },

  pushMenuToSalesChannels: async ({
    organizationKeyName,
    menuId,
  }: PushMenuToSalesChannelsData) => {
    const url = `/organizations/${organizationKeyName}/shop-menus/items/${menuId}/push-menu`;
    const response = await axiosAuthenticated.post<any, AxiosResponse>(url);

    return response;
  },

  getSalesChannelsFromMenu: async (
    organizationKeyName: string,
    menuId?: string
  ): Promise<MenuSalesChannel[]> => {
    const url = `/organizations/${organizationKeyName}/sales-channels/items?menu=${menuId}`;
    const response = await axiosAuthenticated.get<
      any,
      AxiosResponse<SalesChannelsFromMenuSuccessResponse>
    >(url);

    return response.data.data;
  },

  pushMenusForSalesChannels: async ({
    organizationKeyName,
    data,
  }: PushMenusToSalesDestinationsData) => {
    const url = `organizations/${organizationKeyName}/shop-menus/push-to-destinations`;
    const response = await axiosAuthenticated.post<any, AxiosResponse>(
      url,
      data
    );

    return response;
  },

  getMenuRecommendedProducts: async (
    organizationKeyName?: string,
    menuId?: string
  ): Promise<MenuRecommendedProduct[]> => {
    const url = `/organizations/${organizationKeyName}/shop-menus/items/${menuId}/recommended-products`;
    const response = await axiosAuthenticated.get<
      any,
      AxiosResponse<MenuRecommendedProductsSuccessResponse>
    >(url);

    return response.data.data;
  },

  updateMenuRecommendedProducts: async ({
    organizationKeyName,
    menuId,
    recommendedProducts,
  }: UpdateRecommendedProductsPayload) => {
    const url = `/organizations/${organizationKeyName}/shop-menus/items/${menuId}/recommended-products`;
    const response = await axiosAuthenticated.put<any, AxiosResponse>(url, {
      products: recommendedProducts,
    });

    return response;
  },

  getMenuOverwrites: async (organizationKeyName?: string, menuId?: string) => {
    const response = await httpClient<{ data: MenuOverwrite[] }>({
      url: `/organizations/${organizationKeyName}/shop-menus/items/${menuId}/overwrites`,
      method: "GET",
    });
    return response.data;
  },

  createMenuOverwrite: async ({
    organizationKeyName,
    data,
    menuId,
  }: PostPayload<MutateMenuOverwritePayload> & { menuId?: string }) => {
    const response = await httpClient<{ id: string }>({
      url: `/organizations/${organizationKeyName}/shop-menus/items/${menuId}/overwrites`,
      method: "POST",
      data,
    });
    return response.id;
  },

  updateMenuOverwrite: ({
    organizationKeyName,
    data: { id, ...rest },
    menuId,
  }: PutPayload<MutateMenuOverwritePayload> & { menuId?: string }) => {
    return httpClient({
      url: `/organizations/${organizationKeyName}/shop-menus/items/${menuId}/overwrites/${id}`,
      method: "PUT",
      data: rest,
    });
  },

  deleteMenuOverwrite: async ({
    data: { id },
    organizationKeyName,
  }: DeletePayload) => {
    return httpClient({
      method: "DELETE",
      url: `/organizations/${organizationKeyName}/shop-menus/items/overwrites/${id}`,
    });
  },

  getMenuCampaigns: async (organizationKeyName?: string, menuId?: string) => {
    return httpClient<MenuCampaign[]>({
      url: `/organizations/${organizationKeyName}/shop-menus/items/${menuId}/campaigns`,
      method: "GET",
    });
  },

  removeCampaignFromMenu: async ({
    data: { id, menuId },
    organizationKeyName,
  }: DeletePayload<{ menuId: string }>) => {
    return httpClient({
      method: "DELETE",
      url: `/organizations/${organizationKeyName}/shop-menus/items/${menuId}/campaigns/${id}`,
    });
  },

  addCampaignToMenu: async ({
    data: { id, menuId },
    organizationKeyName,
  }: PostPayload<{ id: string; menuId: string }>) => {
    return httpClient({
      method: "POST",
      url: `/organizations/${organizationKeyName}/shop-menus/items/${menuId}/campaigns/${id}`,
    });
  },
};
